import { motion } from 'framer-motion';
import React from 'react';
import { FiAlertTriangle, FiEye, FiKey, FiLock, FiMonitor, FiShield } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const CyberSecuritySection = () => {
  const securityServices = [
    {
      icon: FiShield,
      title: "Threat Detection",
      description: "Advanced monitoring and real-time threat identification",
      level: "Critical"
    },
    {
      icon: FiLock,
      title: "Data Protection",
      description: "End-to-end encryption and secure data management",
      level: "Essential"
    },
    {
      icon: FiEye,
      title: "Security Audits",
      description: "Comprehensive security assessments and compliance checks",
      level: "Recommended"
    },
    {
      icon: FiKey,
      title: "Access Control",
      description: "Multi-factor authentication and identity management",
      level: "Critical"
    },
    {
      icon: FiMonitor,
      title: "24/7 Monitoring",
      description: "Continuous surveillance and incident response",
      level: "Essential"
    },
    {
      icon: FiAlertTriangle,
      title: "Incident Response",
      description: "Rapid response and recovery from security breaches",
      level: "Critical"
    }
  ];

  const getLevelColor = (level) => {
    switch (level) {
      case 'Critical': return 'from-red-500 to-orange-500';
      case 'Essential': return 'from-yellow-500 to-orange-500';
      case 'Recommended': return 'from-green-500 to-blue-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <section id="cyber-security" className="py-20 bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-red-500/10 rounded-full filter blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-orange-500/10 rounded-full filter blur-3xl animate-pulse animation-delay-2000"></div>
        <div className="absolute top-3/4 left-3/4 w-32 h-32 bg-yellow-500/10 rounded-full filter blur-2xl animate-pulse animation-delay-4000"></div>
      </div>

      {/* Floating Security Icons */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-white/5"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 360],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              delay: i * 2,
            }}
          >
            <FiShield size={40 + i * 10} />
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-red-500/20 backdrop-blur-sm border border-red-400/30 rounded-full text-red-300 text-sm font-medium mb-6 font-accent tracking-wide"
          >
            <FiShield className="animate-pulse" />
            <span>Maximum Protection</span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-bold mb-6 font-display tracking-tight"
          >
            <span className="bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
              Cyber Security
            </span>
            <br />
            <span className="text-white">Excellence</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            viewport={{ once: true }}
            className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-body font-light tracking-wide"
          >
            Protect your digital assets with enterprise-grade security solutions. 
            Our comprehensive cybersecurity services safeguard your business from evolving threats.
          </motion.p>
        </motion.div>

        {/* Security Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {securityServices.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              whileHover={{ 
                scale: 1.05,
                y: -5,
                transition: { duration: 0.3 }
              }}
              className="group"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 h-full hover:bg-white/10 transition-all duration-300 relative overflow-hidden">
                {/* Animated border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-500/20 via-orange-500/20 to-yellow-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className={`w-16 h-16 bg-gradient-to-br ${getLevelColor(service.level)} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-12 transition-all duration-300 shadow-lg`}>
                    <service.icon className="text-white" size={28} />
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white font-heading tracking-wide">{service.title}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full font-accent tracking-wide ${
                      service.level === 'Critical' ? 'bg-red-500/20 text-red-300' :
                      service.level === 'Essential' ? 'bg-yellow-500/20 text-yellow-300' :
                      'bg-green-500/20 text-green-300'
                    }`}>
                      {service.level}
                    </span>
                  </div>
                  
                  <p className="text-gray-300 leading-relaxed font-body">{service.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Security Dashboard Mockup */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 md:p-12 mb-16"
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-white mb-4 font-display tracking-tight">Security Operations Center</h3>
            <p className="text-gray-300 text-lg font-body font-light tracking-wide">Real-time monitoring and threat intelligence</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { metric: "Threats Blocked", value: "99.9%", color: "text-green-400" },
              { metric: "Response Time", value: "<5min", color: "text-blue-400" },
              { metric: "Uptime", value: "99.99%", color: "text-purple-400" },
              { metric: "Compliance", value: "100%", color: "text-yellow-400" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center bg-white/5 rounded-xl p-6 border border-white/10"
              >
                <div className={`text-3xl font-bold mb-2 font-display ${stat.color}`}>{stat.value}</div>
                <div className="text-gray-300 text-sm font-body tracking-wide">{stat.metric}</div>
              </motion.div>
            ))}
          </div>

          {/* Threat Level Indicator */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center space-x-4 bg-green-500/20 border border-green-400/30 rounded-full px-6 py-3">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-green-300 font-medium font-body tracking-wide">Current Threat Level: LOW</span>
            </div>
          </motion.div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link to="/contact">
          <motion.button
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 20px 40px rgba(239, 68, 68, 0.4)"
            }}
            whileTap={{ scale: 0.95 }}
            className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-10 py-4 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 relative overflow-hidden group font-accent tracking-wide"
          >
            <span className="relative z-10">Secure Your Business</span>
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </motion.button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default CyberSecuritySection; 